import { Good } from "@/entities/good";
import { GoodVersion } from "@/entities/goodVersion";
import { geniFetch } from "./geniBackend";
import { marketplaceKeyFromApi } from "@/marketplaces/marketplaceApiMapping";
import { enrichGoodVersionImages, normalizeGoodImageList } from "@/utils/goodVersionDisplay";
import { useAuthStore } from "@/stores/auth";
import { refreshKaspiStatuses } from "./kaspiApi/refreshKaspiStatuses";

function hasKaspiApiAccess(): boolean {
  const { kaspi } = useAuthStore().marketplaceConnections;
  return kaspi.connected || Boolean(kaspi.token?.trim());
}

/**
 * Pure reader of the goods list from geni-backend. It performs NO side effects:
 * no WB API calls, no good creation, no wizard mutation. Wildberries goods are imported
 * into the DB asynchronously by card_genius_service (wb-goods-import-service); the frontend
 * only renders what /goods returns. See GC-22 — the previous in-getter import corrupted the
 * active product-creation wizard.
 */
export const getMerchantGoods = async (): Promise<Good[]> => {
  const normalizeGoodContentType = (value: unknown): "card" | "images" => {
    if (value === "images" || value === "photo") return "images";
    return "card";
  };

  // Kaspi: просим backend обновить статусы публикаций «в обработке» ДО загрузки товаров,
  // чтобы ответ /goods уже содержал актуальный metadata.kaspi.status.
  if (hasKaspiApiAccess()) {
    await refreshKaspiStatuses();
  }

  const goodsList = (await geniFetch<any[]>("/goods")) ?? [];
  const versionsList = goodsList.flatMap((g) =>
    (g.versions ?? []).map((v: any) => ({
      field_29: JSON.stringify(v.payload ?? {}),
      profile_id: v.id,
      field_1001683: g.id,
      creation_datetime_unix: Math.floor(new Date(v.createdAt).getTime() / 1000),
    }))
  );

  const formattedGoods: Good[] = [];

  for (const good of goodsList) {
    const field_29 = {
      id: good.id,
      marketplace: marketplaceKeyFromApi(good.marketplace),
      contentType: normalizeGoodContentType(good.contentType),
      characteristics: good.characteristics || [],
      competitors: good.competitors || [],
      images: normalizeGoodImageList(good.images),
      category: good.category,
      brand: good.brand,
    };
    const field_19 = good.metadata || {};
    const goodVersions = versionsList.filter((version) => {
      return version.field_1001683 === field_29.id;
    });

    const formattedVersions = goodVersions.map((version) => {
      const versionField_29 = JSON.parse(version.field_29);
      if (versionField_29?.media?.images) {
        versionField_29.media.images = normalizeGoodImageList(versionField_29.media.images);
      }

      versionField_29.profile_id = version.profile_id;

      const metrics = field_19?.calculatedAnalytics?.[versionField_29.id];
      const createdAt = new Date(version.creation_datetime_unix * 1000);

      return { ...versionField_29, metrics, createdAt } as GoodVersion;
    });

    const latestVersionRaw = field_19?.latestVersion as Partial<GoodVersion> | undefined;
    if (latestVersionRaw && typeof latestVersionRaw === "object" && typeof latestVersionRaw.id === "string") {
      const latestVersion: GoodVersion = {
        ...(latestVersionRaw as GoodVersion),
        media: {
          ...(latestVersionRaw.media || { images: [] }),
          images: normalizeGoodImageList(latestVersionRaw.media?.images),
        },
      };

      const existingIdx = formattedVersions.findIndex((version) => version.id === latestVersion.id);
      if (existingIdx >= 0) {
        const existingVersion = formattedVersions[existingIdx];
        formattedVersions[existingIdx] = {
          ...existingVersion,
          ...latestVersion,
          profile_id: existingVersion.profile_id ?? latestVersion.profile_id,
          createdAt: existingVersion.createdAt ?? latestVersion.createdAt,
        };
      } else {
        formattedVersions.push(latestVersion);
      }
    }

    const goodLevelImages = normalizeGoodImageList(field_29.images);
    const enrichedVersions = formattedVersions.map((version) => enrichGoodVersionImages(version, goodLevelImages));
    enrichedVersions.sort((a, b) => {
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      return tb - ta;
    });
    const syncedGoodImages =
      enrichedVersions.length > 0
        ? normalizeGoodImageList(enrichedVersions[0].media?.images)
        : goodLevelImages;

    const wbSku = field_19.wb?.id as string | undefined;
    const nmId = (field_19.wb?.nmId ?? null) as number | null;
    const subjectId = (field_19.wb?.subjectId ?? null) as number | null;
    const kaspiState = (field_19.kaspi || {}) as {
      sku?: string;
      importCode?: string;
      categoryCode?: string;
      categoryTitle?: string;
      // "published" может встретиться в старых записях БД — трактуем как "На модерации".
      status?: "processing" | "moderation" | "published" | "error";
      // Товар импортирован из магазина Kaspi (kaspi-goods-import-service) — он реально опубликован.
      imported?: boolean;
      error?: string;
    };

    let publicationErrors: string[];
    let publishStatus: Good["publishStatus"];
    if (field_29.marketplace === "kaspi") {
      publicationErrors = kaspiState.error ? [kaspiState.error] : [];
      // Товары, импортированные из магазина Kaspi, реально опубликованы → "Опубликован".
      // Для опубликованных пользователем через наш флоу не показываем "Опубликован": успех по
      // API не гарантирует реальную публикацию, поэтому любой неошибочный статус → "На модерации".
      publishStatus = kaspiState.error || kaspiState.status === "error"
        ? "error"
        : kaspiState.imported
          ? "published"
          : kaspiState.status === "processing" ||
              kaspiState.status === "moderation" ||
              kaspiState.status === "published"
            ? "moderation"
            : "draft";
    } else {
      // WB-статус определяется по данным из БД. Список опубликованных/ошибочных карточек
      // больше не тянется напрямую из браузера — его актуализирует wb-goods-import-service
      // и кладёт ошибки модерации в metadata.wbPublishErrors.
      const wbPublishErrors = Array.isArray(field_19.wbPublishErrors)
        ? (field_19.wbPublishErrors as string[])
        : [];
      publicationErrors = wbPublishErrors;
      publishStatus = wbPublishErrors.length
        ? "error"
        : nmId !== null && nmId !== undefined
          ? "published"
          : field_19.publishStatus === "published"
            ? "published"
            : "draft";
    }

    const normalizedCategory =
      field_29.category && typeof field_29.category === "object"
        ? {
            ...(field_29.category as Record<string, unknown>),
            wb: {
              ...(((field_29.category as Record<string, any>).wb || {}) as Record<string, unknown>),
              id: ((field_29.category as Record<string, any>).wb?.id as number | undefined) || subjectId || undefined,
            },
          }
        : typeof field_29.category === "string" && field_29.category.trim()
          ? {
              wb: {
                name: field_29.category.trim(),
                ...(subjectId ? { id: subjectId } : {}),
              },
            }
          : field_29.category;

    const formattedGood: Good = {
      profile_id: Number(good.id),
      id: field_29.id,
      title: good.title || undefined,
      contentType: field_29.contentType,
      marketplace: field_29.marketplace,
      versions: enrichedVersions,
      publishStatus: publishStatus,
      characteristics: field_29.characteristics || [],
      competitors: field_29.competitors || [],
      images: syncedGoodImages,
      category: normalizedCategory,
      brand: field_29.brand,
      publicationErrors,
      generating: field_19.generating === true,
      contentJobId: field_19.contentJobId ?? undefined,
      contentJobOwnerId: field_19.contentJobOwnerId ?? undefined,
      generationContext: field_19.generationContext ?? undefined,
      publicationData: {
        wb: {
          id: wbSku,
          nmId: nmId,
          subjectId: subjectId || undefined,
          characteristics: field_19.wb?.characteristics,
          sizes: field_19.wb?.sizes,
        },
        kaspi:
          field_29.marketplace === "kaspi"
            ? {
                sku: kaspiState.sku,
                importCode: kaspiState.importCode,
                categoryCode: kaspiState.categoryCode,
                categoryTitle: kaspiState.categoryTitle,
                status: kaspiState.status === "published" ? "moderation" : kaspiState.status,
                error: kaspiState.error,
              }
            : undefined,
      },
      recommendations: field_19.recommendations,
    };

    formattedGoods.push(formattedGood);
  }

  return formattedGoods;
};
